/* eslint-disable max-lines */
// InsightView.tsx — vertical tab layout
import AcademicCapSvg from 'assets/images/team performance/academicCapSvg';
import { Images } from 'core/common/imagePath';
import useFormatCurrency from 'core/utils/formatCurrency';
import useFormatPoints from 'core/utils/formatPoints';
import TeamPerformanceTitle from 'pages/teamPerformance/teamPerformanceTitle';
import React, { useState } from 'react';

/* ── helpers ─────────────────────────────────────────── */
const pct = (a, b) => ((a / b) * 100).toFixed(2);

const severityStyles = {
  warning: {
    bar: 'bg-[#C0CA33]',
    badge: 'text-[#C0CA33] border-[#C0CA33]/20 bg-[#C0CA33]/10',
    dot: 'bg-[#C0CA33]',
    text: 'text-[#C0CA33]',
  },
  positive: {
    bar: 'bg-[#43A047]',
    badge: 'text-[#43A047] border-[#43A047]/20 bg-[#43A047]/10',
    dot: 'bg-[#43A047]',
    text: 'text-[#43A047]',
  },
  negative: {
    bar: 'bg-[#E91E63]',
    badge: 'text-[#E91E63] border-[#E91E63]/20 bg-[#E91E63]/10',
    dot: 'bg-[#E91E63]',
    text: 'text-[#E91E63]',
  },
  neutral: {
    bar: 'bg-slate-200',
    badge: 'text-slate-600 border-slate-300 bg-slate-50',
    dot: 'bg-slate-300',
    text: 'text-slate-600',
  },
};

/**
 * Derive a meaningful visual severity from a block so we never
 * silently fall back to the grey "neutral" style when there is
 * real signal in the data.
 *
 * Rules (in priority order):
 *  1. If the block already has a non-neutral severity → keep it.
 *  2. Rule Performance  → always positive (top contributors are good news).
 *  3. Participant Status → look at the distribution ratios:
 *       capped > 15 %   → warning
 *       no-inc > 40 %   → negative
 *       otherwise        → positive  (healthy distribution)
 *  4. Payout vs Business → derive from payout/sales change directions:
 *       payout ↑ & sales ↓ → negative
 *       both ↑             → warning  (ratio may be rising)
 *       payout ↓ or ratio ↓→ positive
 *       unchanged          → neutral  (no movement)
 *       otherwise          → warning
 */
const computeDisplaySeverity = (block, plan) => {
  if (block.severity && block.severity !== 'neutral') return block.severity;

  const { section } = block;

  if (section.includes('Rule Performance')) return 'positive';

  if (section.includes('Participant Status')) {
    const d = plan?.incentiveDistribution;
    if (!d) return 'positive';
    const total = d.gotIncentive + d.capped + d.noIncentive;
    if (total === 0) return 'positive';
    if (parseFloat(pct(d.capped, total)) > 15) return 'warning';
    if (parseFloat(pct(d.noIncentive, total)) > 40) return 'negative';
    return 'positive';
  }

  if (section.includes('Payout') && section.includes('Business')) {
    const payDir = plan?.payout?.change?.direction;
    const salDir = plan?.sales?.change?.direction;
    // Both unchanged → neutral/stable
    if (payDir === 'unchanged' && salDir === 'unchanged') return 'neutral';
    if (payDir === 'increased' && salDir === 'decreased') return 'negative';
    if (payDir === 'decreased') return 'positive';
    if (payDir === 'increased' && salDir === 'increased') return 'warning';
    return 'warning';
  }

  // Gainers / Fallers
  if (section === 'Gainers') return 'positive';
  if (section === 'Fallers') return 'negative';

  return 'warning'; // last resort — never grey
};

const Hi = ({ children }) => (
  <span className="font-bold text-slate-800">{children}</span>
);

/**
 * ChgTag — renders a directional change badge.
 * Returns null for "unchanged" or missing direction so no badge is shown.
 */
const ChgTag = ({ dir, val }) => {
  if (!dir || dir === 'unchanged') return null;
  const severity = dir === 'increased' ? 'positive' : 'negative';
  const style = severityStyles[severity];
  return (
    <span className={`font-medium ${style.text}`}>
      {dir === 'increased' ? '↑' : '↓'}
      {Math.abs(val)}%
    </span>
  );
};

// Map section to icon
const getSectionIcon = (section) => {
  if (section.includes('Rule Performance'))
    return Images.InsightsRulePerformance;
  if (section.includes('Participant Status')) return Images.InsightsParticipant;
  if (section.includes('Payout') && section.includes('Business'))
    return Images.InsightsPayoutVsBuisness;
  if (section === 'Gainers') return Images.InsightGainer;
  if (section === 'Fallers') return Images.InsightFaller;
  return Images.InsightsTime;
};

/* ── buildInsights ───────────────────────────────────── */
const getFormattedRuleNames = (names) => {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]}, ${names[1]}, and ${names[2]}`;
};

/**
 * Returns human-readable verb phrase for a change direction.
 * "unchanged" → "remained the same"
 */
const getChangeVerb = (direction, increasedVerb, decreasedVerb) => {
  if (direction === 'unchanged') return 'remained the same';
  if (direction === 'increased') return increasedVerb;
  return decreasedVerb;
};

const buildInsights = (plan, hasPreviousPeriod, formatValue) => {
  const blocks = [];
  const dist = plan.incentiveDistribution;
  const total = dist ? dist.gotIncentive + dist.capped + dist.noIncentive : 0;

  if (dist && total > 0) {
    const cappedPct = parseFloat(pct(dist.capped, total));
    const noIncPct = parseFloat(pct(dist.noIncentive, total));
    const avgEarner =
      plan.payout?.current && dist.gotIncentive > 0
        ? formatValue(Math.round(plan.payout.current / dist.gotIncentive))
        : null;

    if (cappedPct > 15) {
      blocks.push({
        section: 'Participant Status',
        severity: 'warning',
        badge: 'Review Cap',
        lines: [
          <React.Fragment key="ps-a">
            <Hi>{dist.gotIncentive} employees</Hi> earned incentives, with{' '}
            <Hi>{dist.capped}</Hi> reaching the cap.
          </React.Fragment>,
          <React.Fragment key="ps-b">
            More than <Hi>{cappedPct}%</Hi> hitting the cap may indicate
            it&apos;s set too low.
          </React.Fragment>,
        ],
      });
    } else if (noIncPct > 40) {
      blocks.push({
        section: 'Participant Status',
        severity: 'negative',
        badge: 'High Dropout',
        lines: [
          <React.Fragment key="ps-a">
            <Hi>{dist.noIncentive} employees</Hi> did not qualify —{' '}
            <Hi>{noIncPct}%</Hi> of participants.
          </React.Fragment>,
          <React.Fragment key="ps-b">
            A high non-qualification rate may signal the threshold is set too
            high.
          </React.Fragment>,
        ],
      });
    } else {
      blocks.push({
        section: 'Participant Status',
        severity: 'neutral', // will be upgraded by computeDisplaySeverity → positive
        badge: null,
        lines: [
          <React.Fragment key="ps-a">
            <Hi>{dist.gotIncentive} participants</Hi> received incentives,{' '}
            <Hi>{dist.capped}</Hi> hit the cap, and <Hi>{dist.noIncentive}</Hi>{' '}
            fell below threshold
            {avgEarner != null ? (
              <>
                , avg payout <Hi>{avgEarner}</Hi>
              </>
            ) : null}
            .
          </React.Fragment>,
        ],
      });
    }
  }

  if (plan.top3Rules?.length > 0) {
    const rules = plan.top3Rules.filter((r) => r.totalValue > 0);
    if (rules.length > 0) {
      const allTotal = rules.reduce((s, r) => s + r.totalValue, 0);
      const isSingle = rules.length === 1;
      const ruleNames = isSingle
        ? rules[0].inputKey
        : getFormattedRuleNames(rules.map((r) => r.inputKey));
      const lines = [
        <React.Fragment key="re-a">
          {isSingle ? (
            <>
              Top contributor: <Hi>{ruleNames}</Hi>.
            </>
          ) : (
            <>
              Top contributors: <Hi>{ruleNames}</Hi>.
            </>
          )}
        </React.Fragment>,
      ];
      if (allTotal > 0) {
        lines.push(
          <React.Fragment key="re-b">
            {isSingle ? (
              <>
                It contributed <Hi>{formatValue(allTotal)}</Hi>.
              </>
            ) : (
              <>
                Together they contributed <Hi>{formatValue(allTotal)}</Hi>.
              </>
            )}
          </React.Fragment>
        );
      }
      blocks.push({
        section: 'Rule Performance',
        severity: 'neutral', // will be upgraded → positive
        badge: null,
        lines,
      });
    }
  }

  if (hasPreviousPeriod && plan.payout != null && plan.sales != null) {
    const { payout: po, sales: sa } = plan;
    const pn = plan.payoutVariableName || 'Payout';
    const sn = plan.salesVariableName || 'Sales';
    const cr =
      po.current && sa.current ? (po.current / sa.current) * 100 : null;
    const pr =
      po.previous && sa.previous ? (po.previous / sa.previous) * 100 : null;
    const rc = cr != null && pr != null ? cr - pr : null;
    const isBad =
      po.change?.direction === 'increased' &&
      sa.change?.direction === 'decreased';

    const payDir = po.change?.direction;
    const salDir = sa.change?.direction;

    // Build payout line — omit change tag when unchanged
    const payoutLine = (
      <React.Fragment key="pvs-a">
        {pn} {getChangeVerb(payDir, 'increased', 'dropped')} from{' '}
        <Hi>{formatValue(po.previous)}</Hi> to{' '}
        <Hi>{formatValue(po.current)}</Hi>
        {payDir !== 'unchanged' ? (
          <>
            {' '}
            (<ChgTag dir={payDir} val={po.change?.value} />)
          </>
        ) : null}{' '}
        vs last cycle.
      </React.Fragment>
    );

    // Build sales line — omit change tag when unchanged
    const salesLine = (
      <React.Fragment key="pvs-b">
        {sn} {getChangeVerb(salDir, 'grew', 'dropped')} from{' '}
        <Hi>{formatValue(sa.previous)}</Hi> to{' '}
        <Hi>{formatValue(sa.current)}</Hi>
        {salDir !== 'unchanged' ? (
          <>
            {' '}
            (<ChgTag dir={salDir} val={sa.change?.value} />)
          </>
        ) : null}{' '}
        vs last cycle.
      </React.Fragment>
    );

    const lines = [payoutLine, salesLine];
    if (rc != null) {
      // Calculate relative change for better context
      const relativeChange = pr > 0 ? (rc / pr) * 100 : null;

      // Stable means little relative movement, not absolute percentage points
      // e.g., less than 10% relative change
      const isStable =
        relativeChange !== null && Math.abs(relativeChange) <= 10;

      if (isStable) {
        lines.push(
          <React.Fragment key="pvs-c">
            Payout-to-revenue ratio is stable at <Hi>{cr?.toFixed(2)}%</Hi>.
          </React.Fragment>
        );
      } else if (rc > 0) {
        lines.push(
          <React.Fragment key="pvs-c">
            Payout-to-revenue ratio{' '}
            <span className={severityStyles.negative.text}>
              rose {rc?.toFixed(2)} percentage points
            </span>{' '}
            (from {pr?.toFixed(2)}% to {cr?.toFixed(2)}%) — payout increased
            despite lower sales volume.
          </React.Fragment>
        );
      } else {
        lines.push(
          <React.Fragment key="pvs-c">
            Payout-to-revenue ratio{' '}
            <span className={severityStyles.positive.text}>
              improved by {Math.abs(rc)?.toFixed(2)} percentage points
            </span>{' '}
            (from {pr?.toFixed(2)}% to {cr?.toFixed(2)}%) — better payout
            efficiency.
          </React.Fragment>
        );
      }
    }

    blocks.push({
      section: 'Payout vs Business',
      severity: isBad ? 'negative' : 'neutral', // neutral will be computed
      badge: isBad ? 'Review Spend' : null,
      lines,
    });
  }

  return blocks;
};

/* ── InsightBlock ────────────────────────────────────── */
const InsightBlock = ({ block, plan }) => {
  // Compute a meaningful (non-grey) severity for every block
  const displaySeverity = computeDisplaySeverity(block, plan);
  const style = severityStyles[displaySeverity];
  const sectionIcon = getSectionIcon(block.section);

  return (
    // White card wrapper
    <div className="bg-white rounded-xl border border-slate-300 shadow-sm p-4">
      <div className="relative pl-4">
        <div
          className={`absolute left-0 top-0.5 bottom-0.5 w-0.5 rounded-full ${style.bar}`}
        />
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[16px] font-semibold text-black-700 leading-none flex items-center">
            <img
              className="w-8 h-8 pr-1"
              src={sectionIcon}
              alt={block.section}
            />
            {block.section}
          </span>
          {block.badge != null && block.badge !== '' ? (
            <span
              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${style.badge}`}
            >
              {block.badge}
            </span>
          ) : null}
        </div>
        <ul className="space-y-1.5">
          {block.lines.map((line, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i} className="flex items-start gap-2">
              <span
                className={`mt-[7px] shrink-0 w-1 h-1 rounded-full ${style.dot}`}
              />
              <p className="text-[16px] leading-relaxed text-black-700">
                {line}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

/* ── tab badge helper ────────────────────────────────── */
const getPlanBadge = (plan, hasPreviousPeriod) => {
  const d = plan.incentiveDistribution;
  const total = d ? d.gotIncentive + d.capped + d.noIncentive : 0;
  if (d && total > 0) {
    if (parseFloat(pct(d.capped, total)) > 15)
      return {
        label: 'Cap',
        cls: 'bg-[#C0CA33]/10 text-[#C0CA33] border border-[#C0CA33]/20',
      };
    if (parseFloat(pct(d.noIncentive, total)) > 40)
      return {
        label: 'Dropout',
        cls: 'bg-[#E91E63]/10 text-[#E91E63] border border-[#E91E63]/20',
      };
  }
  if (
    hasPreviousPeriod &&
    plan.payout?.change?.direction === 'increased' &&
    plan.sales?.change?.direction === 'decreased'
  ) {
    return {
      label: 'Spend',
      cls: 'bg-[#E91E63]/10 text-[#E91E63] border border-[#E91E63]/20',
    };
  }
  return null;
};

/* ── InsightView ─────────────────────────────────────── */
const InsightView = ({ insightData }) => {
  const formatCurrency = useFormatCurrency();
  const formatPoints = useFormatPoints();
  const [activeIdx, setActiveIdx] = useState(0);

  if (!insightData || Object.keys(insightData).length <= 0) return null;

  const { hasPreviousPeriod, plans } = insightData;
  const activePlan = plans[activeIdx];
  const isCash =
    !activePlan.compensationType ||
    activePlan.compensationType.toLowerCase() === 'cash';
  const formatValue = isCash ? formatCurrency : formatPoints;
  const blocks = buildInsights(activePlan, hasPreviousPeriod, formatValue);

  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden h-full flex flex-col">
      {/* header */}
      <div className=" bg-[#E8F1FF] px-4 pt-3.5 pb-3 border-b border-slate-300 flex items-center justify-between shrink-0">
        <TeamPerformanceTitle icon={<AcademicCapSvg />} title="Insights" />
        {plans.length > 1 ? (
          <span className="text-[15px] text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full">
            {plans.length} plans
          </span>
        ) : null}
      </div>

      {/* body: tabs + content */}
      <div className="flex flex-1 min-h-0">
        {/* vertical tab rail — only when multiple plans */}
        {plans.length > 1 ? (
          <div className="w-36 shrink-0 border-r border-slate-100 bg-slate-50/60 flex flex-col gap-0.5 py-2 px-1.5 overflow-y-auto">
            {plans.map((plan, i) => {
              const badge = getPlanBadge(plan, hasPreviousPeriod);
              const isActive = i === activeIdx;
              return (
                <button
                  key={plan.planId}
                  onClick={() => setActiveIdx(i)}
                  className={[
                    'w-full text-left rounded-md px-2.5 py-2 transition-colors',
                    isActive
                      ? 'bg-white border border-[rgb(147,162,231)]'
                      : 'border border-transparent hover:bg-white/70',
                  ].join(' ')}
                >
                  <div
                    className={`text-[11px] font-semibold leading-tight truncate ${isActive ? 'text-blue-700' : 'text-slate-600'}`}
                  >
                    {plan.planName}
                  </div>
                  <div className="text-[9px] uppercase tracking-wide text-slate-400 mt-0.5">
                    {plan.compensationType ?? 'Cash'}
                  </div>
                  {badge != null ? (
                    <span
                      className={`mt-1 inline-block text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${badge.cls}`}
                    >
                      {badge.label}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        ) : null}

        {/* ── insight content — #E8F1FF background ── */}
        <div
          className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
          style={{ backgroundColor: '#E8F1FF' }}
        >
          {blocks.length > 0 ? (
            blocks.map((block, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <InsightBlock key={i} block={block} plan={activePlan} />
            ))
          ) : (
            <p className="text-[16px] text-slate-400 italic">
              No insights available for this plan.
            </p>
          )}
          {hasPreviousPeriod === false ? (
            <p className="text-[16px] text-slate-400 italic pt-1">
              No previous period data — comparative insights are not shown.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default InsightView;
